import React, { useContext, useState } from 'react'
import { updateUserDetail } from '../../models/axiosModel';
import { Grid, TextField, Typography } from '@mui/material';
import Required from './required';
import contactStyles from "./../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import popupStyles from "./../../assets/styles/componentStyles/globalPopupStyles.module.scss";
import globalStyles from "./../../assets/styles/global.module.scss";
import phoneStyles from "./../../assets/styles/componentStyles/phoneComponent.module.scss";
import { UserContext, UserContextPayload } from '../../context/userContext';
import agent from '../../services/api';
import { useGenericStore } from '../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import UserPasswordSection from './userPasswordSection';
import UserTimezoneSection from './userTimezoneSection';
import EmailAddressDisplay from './emailAddressDisplay';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { CountryCode, isValidNumberForRegion, parsePhoneNumberFromString } from 'libphonenumber-js';
import 'react-phone-input-2/lib/style.css';

interface EditUserDetailPopupProps {
    payload: updateUserDetail,
    setPayload: React.Dispatch<React.SetStateAction<updateUserDetail>>,
    renderButton: (text: string, onClick: () => void, className: string | undefined, width: string, disabled?: boolean) => JSX.Element,
    onClickOfCancel: () => void,
    userDetailsFields: {
        key: string;
        label: string;
        value: string;
        placeholder: string;
        isDisabled: boolean;
        isRequired: boolean;
    }[],
    onOpenPasswordForm?: () => void,
    onOpenTimezoneForm?: () => void,
}

interface FieldEditProps {
    label: string;
    fieldKey: string;
    value: string;
    placeholder: string;
    required?: boolean;
    disabled?: boolean;
    onChange: (value: string, data?: CountryData | {}) => void;
}

const FieldEdit: React.FC<FieldEditProps> = ({ label, fieldKey, value, placeholder, required = false, disabled = false, onChange }) => (
    <Grid item xs={12}>
        <Grid container direction="column">
            <Grid item mb={1}>
                <Typography className={popupStyles.popupFieldTypography}>
                    {label} {required && <Required />}
                </Typography>
            </Grid>
            <Grid item>
                {fieldKey === "user_contact_number" ? (
                    <Grid item className={`${globalStyles.fullwidth} ${phoneStyles.phoneInputWrapperForRequestACallback}`}>
                        <PhoneInput
                            value={value}
                            disabled={disabled}
                            placeholder={placeholder}
                            dropdownStyle={{ top: "auto", bottom: "100%", position: "absolute" }}
                            onChange={(val, data) => onChange(val, data)}
                        />
                    </Grid>
                ) : (
                    <TextField
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                        className={`${contactStyles.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                    />
                )}
            </Grid>
        </Grid>
    </Grid>
);

const EditUserDetailPopup: React.FC<EditUserDetailPopupProps> = (props) => {
    
    const { payload, setPayload, renderButton, onClickOfCancel, userDetailsFields, onOpenPasswordForm, onOpenTimezoneForm } = props;
    const { userDetail, flagViewUserDetailPopup, setFlagLoading, handleError, setUserDetail, setFlagUpdateUserDetailPopup, setFlagViewUserDetailPopup } = useContext(UserContext) as UserContextPayload;

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const [flagValidPhoneNumber, setFlagValidPhoneNumber] = useState<boolean>(true);

    const normalizePhoneNumber = (phone?: string) => (phone ?? "").replace(/\D/g, "");

    const onChangeOfField = (value: string, key: keyof updateUserDetail, data?: CountryData | {}) => {
        let updatedValue = value;

        if (key === "user_contact_number") {
            updatedValue = value ? `+${value}` : "";
            const phoneNumber = parsePhoneNumberFromString(updatedValue);

            if (!updatedValue) {
                setFlagValidPhoneNumber(true);
            } else if (phoneNumber && data && "countryCode" in data) {
                const valid = isValidNumberForRegion(phoneNumber.number, (data.countryCode.toUpperCase()) as CountryCode);
                setFlagValidPhoneNumber(valid);
            } else {
                setFlagValidPhoneNumber(false);
            }
        }

        setPayload((prev) => ({ ...prev, [key]: updatedValue }));
    };

    const isSaveAndContinueDisabled = () => !payload.user_first_name?.trim() || !flagValidPhoneNumber;

    const isSaveButtonDisabled = () => {
        if (!payload.user_first_name?.trim() || !flagValidPhoneNumber) {
            return true;
        }
        
        const isFirstNameUnchanged = payload.user_first_name === userDetail?.user_first_name;
        const isLastNameUnchanged = payload.user_last_name === userDetail?.user_last_name;
        const isContactNumberUnchanged = normalizePhoneNumber(payload.user_contact_number) === normalizePhoneNumber(userDetail?.user_phone_number);
        
        return isFirstNameUnchanged && isLastNameUnchanged && isContactNumberUnchanged;
    };

    const onClickOfSaveBtn = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        
        try {
            const res = await agent.updateUserDetails.put(payload);
            
            if (!res.status) {
                handleError(res);
            } else {
                showSuccess("User Details Updated Successfully.")
                setUserDetail((prev) => ({
                    ...prev,
                    user_first_name: payload.user_first_name ?? "",
                    user_last_name: payload.user_last_name ?? "",
                    user_phone_number: payload.user_contact_number ?? "",
                }));
            }
            
            setFlagUpdateUserDetailPopup(false);
            setFlagViewUserDetailPopup(false);
        } finally {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    };

    return (
        <>
            {userDetailsFields?.map((field) => {
                if (field?.key === "user_email") {
                    return <EmailAddressDisplay key={field.key} email={field?.value} />;
                }

                return (
                    <FieldEdit
                        key={field?.key}
                        fieldKey={field?.key}
                        label={field?.label}
                        value={field?.value}
                        placeholder={field?.placeholder}
                        required={field?.isRequired}
                        disabled={field?.isDisabled}
                        onChange={(val, data) => onChangeOfField(val, field?.key as keyof updateUserDetail, data)}
                    />  
                );
            })}

            {!flagViewUserDetailPopup && onOpenTimezoneForm && (
                <UserTimezoneSection
                    timezoneName={userDetail?.timezone_name}
                    onClickTimezoneAction={onOpenTimezoneForm}
                />
            )}

            {!flagViewUserDetailPopup && onOpenPasswordForm && (
                <UserPasswordSection
                    isPasswordSet={userDetail?.flag_password}
                    onClickPasswordAction={onOpenPasswordForm}
                />
            )}

            <Grid item xs={12} mt={2}>
                <Grid container gap="12px" direction="row" flexWrap="nowrap" justifyContent="center">
                    {renderButton("Cancel", onClickOfCancel, 'borderedButtonStyle', "130px")}
                    {flagViewUserDetailPopup ? 
                        <>
                            {renderButton("Save", onClickOfSaveBtn, 'primaryButtonStyle', "130px", isSaveButtonDisabled())}
                        </>
                        :
                        <>
                            {renderButton("Save & Continue", onClickOfSaveBtn, 'primaryButtonStyle', "170px", isSaveAndContinueDisabled())}
                        </>
                    }
                </Grid>
            </Grid>
        </>
    )
}

export default EditUserDetailPopup
